import Text from "@/components/ui/text";
import React, { useId } from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  TooltipProps,
  LabelList
} from "recharts";
import {
  NameType,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";
import { isEmpty } from "lodash";
import {
  DreamerStackBarChartDataType,
  StackSegmentConfig
} from "@/components/DreamerCharts/types";
import { segmentColorAt } from "@/components/DreamerCharts/chartColors";
import { formatNumber } from "@/utils/numbers";
import LegendWithMore from "@/components/DreamerCharts/LegendWithMore";

interface Props {
  height: number | string;
  chartData: DreamerStackBarChartDataType[];
  segments: StackSegmentConfig[];
  tooltipYpos?: number;
  isPercentage?: boolean;
  chartClassName?: string;
  legendClassName?: string;
  showLegend?: boolean;
  compact?: boolean;
}

const BottomBar = (props: any) => {
  const { x, y, width, height, fill } = props;
  const effectiveHeight = Math.max(height || 0, 1);
  return (
    <rect x={x} y={y} width={width} height={effectiveHeight} fill={fill} />
  );
};

const MiddleBar = (props: any) => {
  const { x, y, width, height, fill } = props;
  const effectiveHeight = Math.max(height || 0, 1);
  return (
    <rect x={x} y={y} width={width} height={effectiveHeight} fill={fill} />
  );
};

const TopBar = (props: any) => {
  const { x, y, width, height, fill } = props;
  const cornerRadius = 3;
  const effectiveHeight = Math.max(height || 0, cornerRadius * 2);
  const adjustedY = y + ((height || 0) - effectiveHeight);

  return (
    <path
      d={`
        M ${x},${adjustedY + effectiveHeight}
        L ${x},${adjustedY + cornerRadius}
        Q ${x},${adjustedY} ${x + cornerRadius},${adjustedY}
        L ${x + width - cornerRadius},${adjustedY}
        Q ${x + width},${adjustedY} ${x + width},${adjustedY + cornerRadius}
        L ${x + width},${adjustedY + effectiveHeight}
        Z
      `}
      fill={fill}
    />
  );
};

const formatStackValue = (raw: number, isPercentage?: boolean) => {
  const { value, modifier } = formatNumber(raw, 2);
  return `${value}${modifier}${isPercentage ? "%" : ""}`;
};

const StackedTooltip = ({
  active,
  payload,
  segments,
  isPercentage
}: TooltipProps<ValueType, NameType> & {
  segments: StackSegmentConfig[];
  isPercentage?: boolean;
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload as DreamerStackBarChartDataType;
    const total = segments.reduce((sum, segment) => {
      const value = data[segment.dataKey];
      return sum + (typeof value === "number" ? value : 0);
    }, 0);

    return (
      <div className="bg-surface-elevation3 border border-border-border1 rounded-lg p-2 shadow-lg">
        <Text variant="smBody" className="text-primary font-semibold mb-1">
          {data.label}
        </Text>
        {segments.map((segment) => {
          const value = data[segment.dataKey];
          const numValue = typeof value === "number" ? value : 0;
          return (
            <div key={segment.dataKey} className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: segment.color }}
              />
              <Text variant="smBody" className="text-secondary">
                {segment.label}: {formatStackValue(numValue, isPercentage)}
              </Text>
            </div>
          );
        })}
        <div className="border-t border-border-border1 mt-1 pt-1">
          <Text variant="smBody" className="text-primary">
            Total: {formatStackValue(total, isPercentage)}
          </Text>
        </div>
      </div>
    );
  }
  return null;
};

const DreamerStackBarChart: React.FC<Props> = ({
  height,
  chartData,
  segments,
  tooltipYpos = 10,
  isPercentage = true,
  chartClassName,
  legendClassName,
  showLegend = true,
  compact = false
}) => {
  // SVG <defs> ids are document-global — without a per-instance prefix two
  // stacked charts on the same page would share gradients and render the
  // wrong segment colors.
  const gradientIdPrefix = useId();

  if (isEmpty(chartData)) return null;

  const barSize =
    chartData?.length === 1 ? 80 : chartData?.length <= 4 ? 32 : 16;

  const dataWithTotal = chartData.map((item) => ({
    ...item,
    total: segments.reduce((sum, segment) => {
      const value = item[segment.dataKey];
      return sum + (typeof value === "number" ? value : 0);
    }, 0)
  }));

  const getBarShape = (index: number) => {
    if (index === 0) return BottomBar;
    if (index === segments.length - 1) return TopBar;
    return MiddleBar;
  };

  const resolvedSegments = segments.map((segment, index) => ({
    ...segment,
    color: segmentColorAt(index, segment.color)
  }));

  const showOnlyEndLabels = compact && chartData.length > 6;
  const lastIndex = chartData.length - 1;

  const containerHeight = typeof height === "number" ? `${height}px` : height;

  return (
    <div
      className="flex flex-col w-full min-h-0"
      style={{ height: containerHeight }}
    >
      <div className="flex-1 min-h-0 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart
            data={dataWithTotal}
            margin={{ left: 25, right: 25, top: 25 }}
            className={chartClassName}
          >
            <defs>
              {resolvedSegments.map((segment, index) => (
                <linearGradient
                  key={`gradient-${index}`}
                  id={`${gradientIdPrefix}-stack-gradient-${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={segment.color} stopOpacity={1} />
                  <stop
                    offset="100%"
                    stopColor={segment.color}
                    stopOpacity={0.8}
                  />
                </linearGradient>
              ))}
            </defs>

            <XAxis
              dataKey="label"
              interval={0}
              tickLine={false}
              axisLine={{ stroke: "var(--widget)" }}
              tick={
                showOnlyEndLabels
                  ? (props: { x: number; y: number; payload: { value: string }; index: number }) => {
                      if (props.index !== 0 && props.index !== lastIndex) {
                        return <g />;
                      }
                      return (
                        <text
                          x={props.x}
                          y={props.y}
                          dy="0.71em"
                          textAnchor="middle"
                          fill="var(--secondary)"
                          fontSize={10}
                        >
                          {props.payload.value}
                        </text>
                      );
                    }
                  : { fill: "var(--secondary)", fontSize: 10 }
              }
            />
            <YAxis
              tickLine={false}
              axisLine={{ stroke: "var(--widget)" }}
              tick={{ fill: "var(--secondary)", fontSize: 10 }}
              tickFormatter={(tick) => {
                const { value, modifier } = formatNumber(Number(tick), 2);
                return `${value}${modifier}${isPercentage ? "%" : ""}`;
              }}
              width={30}
              domain={isPercentage ? [0, 100] : undefined}
            />

            {resolvedSegments.map((segment, index) => (
              <Bar
                key={segment.dataKey}
                dataKey={segment.dataKey}
                stackId="stack"
                fill={`url(#${gradientIdPrefix}-stack-gradient-${index})`}
                shape={getBarShape(index)}
                barSize={barSize}
              >
                {index === segments.length - 1 && (
                  <LabelList
                    dataKey="total"
                    position="top"
                    formatter={(value: number) =>
                      formatStackValue(value, isPercentage)
                    }
                    style={{
                      fill: "var(--secondary)",
                      fontSize: 10,
                      fontWeight: 500
                    }}
                  />
                )}
              </Bar>
            ))}

            <RechartsTooltip
              content={
                <StackedTooltip
                  segments={resolvedSegments}
                  isPercentage={isPercentage}
                />
              }
              cursor={{ fill: "transparent" }}
              position={{ y: tooltipYpos }}
            />
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>

      {showLegend && (
        <LegendWithMore
          items={resolvedSegments.map((segment) => ({
            key: segment.dataKey,
            label: segment.label,
            color: segment.color
          }))}
          className={legendClassName}
        />
      )}
    </div>
  );
};

export default DreamerStackBarChart;
